// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app
//   https://api.orioralabs.com/api/apps/workout_tracker/summarize_trends
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK).
//
// Workout data stays LOCAL. We no longer ship raw set-by-set history; instead Rust
// computes DERIVED SIGNALS (small aggregates — volume/intensity slopes, split
// alternation, set-level fatigue, per-lift trends, frequency, PR counts) PLUS a set of
// pre-ranked, full-sentence OBSERVATIONAL facts (figures + tone, e.g. recent-vs-own-
// baseline deltas, top-set shifts, rest cadence) and only those leave the device. The
// brain weaves them into 2-3 SPECIFIC patterns the user couldn't spot at a glance —
// never advice, never prescription. The same facts render as on-device receipts.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { TrendSignals, HeadlineFact } from "./store";

const SERVER = "https://api.orioralabs.com";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/workout_tracker/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "AI is paused — add funds to continue.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in your Oriora account to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

// ── The one AI feature: 14-day training-trend summary (now signal-driven) ─────
// The server prompt is fixed (see oriora-server/src/routes/catalog-apps/workout-tracker.ts).
//   input  : { signals: TrendSignals }  ← derived aggregates, computed locally in Rust
//   returns: { trends: { summary_text, volume_trend?, freq_per_week?, top_lifts? }, amount_usd, amount_units }
// The model writes summary_text from the signals; the chips below it are filled from
// the SAME signals locally, so the UI stays specific even if the model omits a field.

export type VolumeTrend = "increasing" | "stable" | "decreasing";

// Chips the UI renders. summary_text comes from the model; the metric fields are
// taken from the model when present, otherwise back-filled from the local signals
// so the result is always specific and never blank.
export interface TrendSummary {
  summary_text: string;
  volume_trend?: VolumeTrend;
  freq_per_week?: number;
  top_lifts?: Record<string, number>;
  // richer derived chips, surfaced locally from the signals object
  split?: { groups: string[]; alternates: boolean };
  set_fatigue?: { declining: number; measured: number; avg_reps_drop: number };
  intensity?: { first: number; last: number; direction: VolumeTrend };
  prs_in_window?: number;
  // The pre-ranked observational sentences the summary was drawn from (computed on-device).
  // Shown verbatim under the summary as receipts; never depends on the model.
  facts?: HeadlineFact[];
}

export interface TrendResult {
  trends: TrendSummary;
  amount_usd?: number;
  amount_units?: number;
}

const isDir = (v: unknown): v is VolumeTrend =>
  (["increasing", "stable", "decreasing"] as const).includes(v as VolumeTrend);

// Top lifts pulled straight from the derived signals (most-trained first, est-1RM).
function topLiftsFromSignals(s: TrendSignals): Record<string, number> {
  const out: Record<string, number> = {};
  for (const l of s.per_lift.slice(0, 3)) {
    if (Number.isFinite(l.best_est_1rm) && l.best_est_1rm > 0) out[l.exercise] = l.best_est_1rm;
  }
  return out;
}

export async function summarizeTrends(signals: TrendSignals): Promise<TrendResult> {
  if (!signals || signals.workouts <= 0) {
    throw new BrainError("unavailable", "Log at least one workout in the last 14 days first.");
  }
  const r = await brainCall<{ trends?: unknown; amount_usd?: number; amount_units?: number }>(
    "summarize_trends",
    { signals },
  );
  const t = (r.trends ?? {}) as Partial<TrendSummary>;

  // Prefer the model's field; fall back to the locally-computed signal so chips
  // stay populated and specific even when the model returns only summary_text.
  const modelTop =
    t.top_lifts && typeof t.top_lifts === "object"
      ? Object.fromEntries(
          Object.entries(t.top_lifts as Record<string, unknown>)
            .filter(([, v]) => Number.isFinite(Number(v)))
            .map(([k, v]) => [k, Number(v)]),
        )
      : undefined;
  const localTop = topLiftsFromSignals(signals);

  // Defensive: some models occasionally return the WHOLE structured object stringified as the
  // value of summary_text instead of following the "plain sentence" instruction — seen live
  // testing this app (deepseek-v4-flash did it once, cohere/command-r7b didn't on an identical
  // request). Unwrap rather than show raw JSON to the user.
  let summaryText = String(t.summary_text ?? "No summary available.");
  if (summaryText.trim().startsWith("{")) {
    try {
      const nested = JSON.parse(summaryText) as Partial<TrendSummary>;
      if (typeof nested.summary_text === "string") summaryText = nested.summary_text;
    } catch { /* not JSON after all — show it as-is */ }
  }

  const trends: TrendSummary = {
    summary_text: summaryText,
    volume_trend: isDir(t.volume_trend) ? t.volume_trend : (signals.volume.direction as VolumeTrend),
    freq_per_week: Number.isFinite(Number(t.freq_per_week))
      ? Number(t.freq_per_week)
      : signals.frequency.sessions_per_week,
    top_lifts: modelTop && Object.keys(modelTop).length ? modelTop : (Object.keys(localTop).length ? localTop : undefined),
    split: { groups: signals.split.session_groups, alternates: signals.split.alternates },
    set_fatigue: signals.set_fatigue.exercises_measured > 0
      ? {
          declining: signals.set_fatigue.exercises_declining,
          measured: signals.set_fatigue.exercises_measured,
          avg_reps_drop: signals.set_fatigue.avg_reps_drop,
        }
      : undefined,
    intensity: signals.intensity
      ? {
          first: signals.intensity.first,
          last: signals.intensity.last,
          direction: signals.intensity.direction as VolumeTrend,
        }
      : undefined,
    prs_in_window: signals.prs_in_window > 0 ? signals.prs_in_window : undefined,
    // The on-device receipts behind the summary — always local, never model-dependent.
    facts: Array.isArray(signals.headline_facts) && signals.headline_facts.length
      ? signals.headline_facts
      : undefined,
  };
  return { trends, amount_usd: r.amount_usd, amount_units: r.amount_units };
}

// ── AI-drafted training programs (PROGRAM-DESIGN.md) ────────────────────────────
// A DELIBERATE, explicit, user-initiated action — distinct from summarize_trends both in
// billing (own action name) and in the safety fence (bypasses the chat advice-fence entirely,
// same as summarize_trends already does; see chat/brain.ts). Prescriptive by design: this is
// the one place the app is SUPPOSED to tell you what to lift, because the user asked for exactly
// that, with stated goals, and reviews/edits the result before anything is saved.
import type { TrainingProfile, ProgramExercise, LiftSignal, ExercisePR } from "./store";

export interface DraftProgramSession {
  day_index: number;
  label: string;
  exercises: ProgramExercise[];
}
export interface DraftProgramWeek {
  week_index: number;
  sessions: DraftProgramSession[];
}
export interface DraftProgramResult {
  program_name: string;
  weeks: DraftProgramWeek[];
  rationale: string;
  amount_usd?: number;
  amount_units?: number;
}

function isValidDraftShape(v: unknown): v is Omit<DraftProgramResult, "amount_usd" | "amount_units"> {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  if (typeof o.program_name !== "string" || typeof o.rationale !== "string" || !Array.isArray(o.weeks)) return false;
  return o.weeks.every((w) => {
    if (!w || typeof w !== "object") return false;
    const wk = w as Record<string, unknown>;
    return typeof wk.week_index === "number" && Array.isArray(wk.sessions) && wk.sessions.every((s) => {
      if (!s || typeof s !== "object") return false;
      const sess = s as Record<string, unknown>;
      return typeof sess.day_index === "number" && typeof sess.label === "string" && Array.isArray(sess.exercises);
    });
  });
}

// draft_program is genuinely slow (a real LLM generating a structured multi-week plan, not a few
// sentences) — the server sends whitespace heartbeats while it's in flight so the connection
// never goes silent long enough to hit a proxy time-to-first-byte timeout (found live 2026-07-30:
// even a 1-week ask took ~14s and 502'd before this fix). Since the HTTP status is locked to 200
// once those heartbeat bytes flush, the server encodes failures (`insufficient_funds` etc.) IN the
// JSON body via `_status` instead of a real status code — this bespoke fetch reads that, unlike
// the shared brainCall() (fine for every other endpoint, which returns fast enough to never need
// heartbeats).
async function draftProgramCall(body: unknown): Promise<Record<string, unknown>> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  let res: Response;
  try {
    res = await fetch("https://api.orioralabs.com/api/apps/workout_tracker/draft_program", {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch {
    throw new BrainError("unavailable", "AI is temporarily unavailable.");
  }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 400) throw new BrainError("unavailable", "Set up your training profile first.");
  if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
  const text = (await res.text()).trim(); // heartbeats are leading whitespace; trim, then parse
  let parsed: Record<string, unknown>;
  try { parsed = JSON.parse(text); } catch { throw new BrainError("unavailable", "AI is busy — please try again."); }
  const status = typeof parsed._status === "number" ? parsed._status : undefined;
  if (status === 402) throw new BrainError("insufficient_funds", String(parsed.message ?? "Add funds in your Oriora account to keep using AI."));
  if (status === 403) throw new BrainError("managed_ai_blocked", String(parsed.message ?? "Connect a vendor key in Oriora settings to use AI for this app."));
  if (status !== undefined) throw new BrainError("unavailable", String(parsed.message ?? parsed.error ?? "AI request failed."));
  return parsed;
}

/**
 * Draft an AI-designed program from the user's stated goals + real training history. The ONE
 * billed AI feature that PRESCRIBES rather than describes — explicit action, reviewed and
 * possibly edited by the user before anything is saved (see ProgramReview.tsx). Retries once on a
 * malformed response before giving up, rather than ever showing broken output to the user.
 * weeksRequested is capped at 2 server-side (see PROGRAM-DESIGN.md) — default 1.
 */
export async function draftProgram(
  profile: TrainingProfile,
  perLift: LiftSignal[],
  prs: ExercisePR[],
  weeksRequested = 1,
): Promise<DraftProgramResult> {
  const body = { profile, per_lift: perLift, prs, weeks_requested: weeksRequested };
  for (let attempt = 1; attempt <= 2; attempt++) {
    const r = await draftProgramCall(body);
    if (isValidDraftShape(r)) {
      return { program_name: r.program_name, weeks: r.weeks as DraftProgramWeek[], rationale: r.rationale, amount_usd: (r as { amount_usd?: number }).amount_usd, amount_units: (r as { amount_units?: number }).amount_units };
    }
    if (attempt === 2) throw new BrainError("unavailable", "Couldn't draft a program right now — please try again.");
  }
  throw new BrainError("unavailable", "Couldn't draft a program right now — please try again.");
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/workout_tracker/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/workout_tracker/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/workout_tracker/store route (distinct from the whole-collection `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login defers to the ecosystem web login; session returns via the deep link (App.tsx).
export const openWebLogin = () => invoke<void>("open_external", { url: "https://orioralabs.com/login?return=workout-tracker" });
